import { useEffect, useMemo, useState } from "react";
import { ImagePlus, X } from "lucide-react";

import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FEEDBACK_LIMITS } from "@/config/dropdownData";
import { cn } from "@/lib/utils";

const INPUT_ID = "feedback-image-input";

/**
 * Add `incoming` files to `current`, or say why not — with the limits and the
 * words the server refuses with, so a refusal reads the same either side.
 *
 * Takes the good files in order up to the cap and reports the first problem,
 * rather than dropping the whole pick for one bad file.
 *
 * @returns {{ files: File[], error: string | null }}
 */
export const addFeedbackImages = (current, incoming) => {
  const files = [...current];
  let error = null;
  for (const file of incoming) {
    if (!FEEDBACK_LIMITS.imageMimeTypes.includes(file.type)) {
      error ??= FEEDBACK_LIMITS.messages.imageInvalid;
    } else if (file.size > FEEDBACK_LIMITS.imageMaxBytes) {
      error ??= FEEDBACK_LIMITS.messages.imageTooLarge;
    } else if (files.length >= FEEDBACK_LIMITS.maxImages) {
      error ??= FEEDBACK_LIMITS.messages.tooManyImages;
    } else {
      files.push(file);
    }
  }
  return { files, error };
};

/**
 * Screenshots for a piece of feedback: an Add button, a thumbnail per file
 * with its own remove, and the refusal under them when a pick breaks a limit.
 * Controlled — the form owns the list.
 */
const FeedbackImagePicker = ({ value = [], onChange, disabled = false, className = "" }) => {
  const [error, setError] = useState(null);
  const previews = useMemo(() => value.map((file) => URL.createObjectURL(file)), [value]);
  useEffect(() => () => previews.forEach((url) => URL.revokeObjectURL(url)), [previews]);

  const full = value.length >= FEEDBACK_LIMITS.maxImages;

  const handlePick = (e) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (!picked.length) return;
    const { files, error: refusal } = addFeedbackImages(value, picked);
    setError(refusal);
    onChange(files);
  };

  const remove = (index) => {
    setError(null);
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex flex-wrap items-center gap-2">
        {value.map((file, index) => (
          <div key={`${file.name}-${index}`} className="relative size-16 overflow-hidden rounded-lg border border-border-primary">
            <img src={previews[index]} alt={file.name} className="size-full object-cover" />
            <Button
              type="button"
              variant="secondary"
              size="icon-xs"
              aria-label={`Remove ${file.name}`}
              disabled={disabled}
              onClick={() => remove(index)}
              className="absolute right-0.5 top-0.5 rounded-full"
            >
              <X />
            </Button>
          </div>
        ))}

        <Input
          id={INPUT_ID}
          type="file"
          multiple
          accept={FEEDBACK_LIMITS.imageMimeTypes.join(",")}
          className="hidden"
          onChange={handlePick}
          data-testid="feedback-image-input"
        />
        <Button type="button" variant="outline" size="sm" disabled={disabled || full} onClick={() => document.getElementById(INPUT_ID)?.click()} className="rounded-lg">
          <ImagePlus />
          Add image
        </Button>
      </div>

      <p className="text-xs text-text-secondary">PNG or JPEG, up to {FEEDBACK_LIMITS.maxImages} images, 5 MB each.</p>
      {error && (
        <p role="alert" className="text-xs text-status-error">
          {error}
        </p>
      )}
    </div>
  );
};

export default FeedbackImagePicker;
